import { FC, useCallback } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button } from '@new/common/ui/Button/Button';
import { Calendar } from '@new/common/ui/Calendar/Calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@new/common/ui/Popover/Popover';
import { format } from 'date-fns';
import { DATE_FORMAT_LONG } from '../../lib/constants';

interface DatePickerFieldProps {
  label: string;
  value: Date | undefined;
  onChange: (date: Date | undefined) => void;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  disabled?: (date: Date) => boolean;
  placeholder?: string;
}

export const DatePickerField: FC<DatePickerFieldProps> = ({
  label,
  value,
  onChange,
  isOpen,
  onOpenChange,
  disabled,
  placeholder = 'Select date',
}) => {
  const handleSelect = useCallback(
    (date: Date | undefined) => {
      onChange(date);
      onOpenChange(false);
    },
    [onChange, onOpenChange]
  );

  return (
    <div className="min-w-0 flex flex-col gap-2">
      <label className="block text-sm font-medium text-(--neutral-white-NW300)">{label}</label>
      <Popover open={isOpen} onOpenChange={onOpenChange}>
        <PopoverTrigger
          render={
            <Button
              variant="ghost"
              className="w-full border! border-(--neutral-black-NB300)! justify-between overflow-hidden"
            >
              <span className="truncate">
                {value ? format(value, DATE_FORMAT_LONG) : placeholder}
              </span>
              <ChevronDown className="w-4 h-4 ml-2 shrink-0" />
            </Button>
          }
        />
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" selected={value} onSelect={handleSelect} disabled={disabled} />
        </PopoverContent>
      </Popover>
    </div>
  );
};
